import {Injectable} from '@angular/core';
import {RestApiService} from '../../../shared/services/rest-api.service';
import {ConfigurationService} from './configuration/configuration.service';
import {v4 as uuid} from 'uuid';
import * as _ from 'lodash';
import {RegistrationData} from '../../models/registration-data.model';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {RegistrationService} from './registration.service';
import {BusinessParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {LanguageService} from '../../../shared/services/language.service';
import {RequestHeader} from '../../../shared/models/request-offer/request-header.model';
import {ProcessService} from '../process.service';
import {ProcessRequest} from '../../models/process-request.model';
import {InsurerDataFormStateService} from './section-services/insurer-data/insurer-data-form-state.service';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {RequestMetricResult} from '../../../shared/components/models/metrics/request-metric-result.enum';
import {SalesChannel} from '../../consts/sales-channel.enum';
import {OlxAnalyticsService} from '../../../shared/services/olx-analytics.service';
import {ApplicationEvent, ApplicationEventsService} from '../../../shared/services/application-events.service';
import {SelectedOfferService} from './section-services/selected-offer/selected-offer.service';
import {MetricsService, RequestMetricName} from '../../../shared/services/metrics.service';
import {Ga4CustomEventsHelper} from '../../utils/ga4-custom-events-helper';
import {OfferPrimaryCoverageCode} from '../../consts/offer-primary-coverage-code.enum';
import {PaymentMethod} from '../../consts/payment-method.enum';
import {ApiParamsService} from '../api-params.service';
import {ApiRequestKey} from '../../../shared/enums/api-request-key.enum';

@Injectable()
export class OrderPolicyService {

  private readonly CLASSNAME = 'OrderPolicyService';
  private readonly BLIK_ONLINE_METHODS: PaymentMethod[] = [
      PaymentMethod.CARD,
      PaymentMethod.BLIK,
      PaymentMethod.ONLINE_TRANSFER,
      PaymentMethod.GOOGLE_PAY,
      PaymentMethod.APPLE_PAY,
  ];
  private readonly API_REQUEST_KEY: ApiRequestKey = ApiRequestKey.POST_REGISTRATION;

  get registrationEmail(): string {
    return this.registrationService.registration.contact.email;
  }

  constructor(private api: RestApiService,
              private configuration: ConfigurationService,
              private metricsService: MetricsService,
              private registrationService: RegistrationService,
              private languageService: LanguageService,
              private olxAnalyticsService: OlxAnalyticsService,
              private processService: ProcessService,
              private eventsService: ApplicationEventsService,
              private selectedOfferService: SelectedOfferService,
              private insurerDataFormStateService: InsurerDataFormStateService,
              private applicationStateService: ApplicationStateService,
              private apiParamsService: ApiParamsService) {
  }

  public submitOrderPolicy(): Promise<any> {
    let registration: RegistrationData = _.cloneDeep(this.registrationService.registration);
    let uri = this.configuration.uiConfig.submitOrderPolicyEndpoint;

    if (!!this.registrationEmail) {
      this.applicationStateService.createAnalyticsUserId(this.registrationEmail);
    }

    const headerReq = this.createHeader();
    const bodyReq = this.createBody(registration);

    let body: ProcessRequest = {
      header: headerReq,
      body: bodyReq
    };

    this.sendPaymentEventMetric();

    const metricName = this.prepareMetrics(body);
    this.applicationStateService.orderPolicySubmitted = true;

    this.eventsService.sendEvent(ApplicationEvent.PREPARE_REGISTRATION, this.buildPayloadForEvent());
    Ga4CustomEventsHelper.paymentInitEvent.emit(<PaymentMethod>this.registrationService.registration.paymentMethod);

    return this.api.post(uri, body)
      .then(() => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.SUCCESS
        });
        return Promise.resolve();
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
        return Promise.reject(error);
      });
  }

  getOfferCoverages(): string[] {
    return this.selectedOfferService.selectedOffer.coverages
        .filter(coverage => {
          if (Object.keys(OfferPrimaryCoverageCode).includes(coverage.code)) {
            const primaryBoxDestiny = coverage.destinies.find(destiny => destiny.code === 'PRIMARY_BOX');
            return primaryBoxDestiny.selectedOption === `${coverage.code}_YES`;
          }
        })
        .map(coverage => coverage.code);
  }

  private buildPayloadForEvent(): any {
    return {
      calculationId: this.applicationStateService.getBusinessParameterValue(BusinessParamName.CALCULATION_ID),
      offerId: this.registrationService.registration.offerId,
      orderId: this.applicationStateService.getBusinessParameterValue(BusinessParamName.ORDER_ID),
      insuranceCompanyName: this.applicationStateService.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE),
      offerAmount: this.getOfferAmount(),
      paymentMethod: this.registrationService.registration.paymentMethod,
      coverages: this.getOfferCoverages()
    };
  }

  private getOfferAmount(): string {
    const {premiumId} = this.registrationService.registration;
    const selectedPremium = this.selectedOfferService.selectedOffer.premiums.find(premium => premium.id === premiumId);
    return selectedPremium ? selectedPremium.amount.amount + '' : null;
  }

  private createHeader(): RequestHeader {
    const requestId = uuid();
    this.applicationStateService.saveBusinessParameter(BusinessParamName.ORDER_ID, uuid());

    return {
      languageCode: this.languageService.getDefaultLanguage().toUpperCase(),
      requestId: requestId,
      responseId: requestId,
      requestDate: new Date(),
      responseDate: new Date(),
      processContext: {
        processId: this.processService.processId,
        businessParameters: this.apiParamsService.createBusinessParams({
          requestKey: this.API_REQUEST_KEY
        })
      },
      technicalParameters: this.apiParamsService.createTechnicalParams({
        requestKey: this.API_REQUEST_KEY
      })
    };
  }

  private createBody(registration: RegistrationData) {
    registration = this.processRegistrationForOrderPolicy(registration);
    return {
      registration: registration
    };
  }

  private processRegistrationForOrderPolicy(registration: RegistrationData): RegistrationData {
    const { paymentMethod } = registration;
    if (this.shouldMapPaymentMethodToBlikOnline(<PaymentMethod>paymentMethod)) {
      registration.paymentMethod = PaymentMethod.BLIK_ONLINE;
    }

    registration = this.removeReadonlyAgreements(registration);
    registration = this.insurerDataFormStateService.mapAddressData(registration);

    return registration;
  }

  private shouldMapPaymentMethodToBlikOnline(method: PaymentMethod): boolean {
    return this.BLIK_ONLINE_METHODS.includes(method);
  }

  private removeReadonlyAgreements(registration: RegistrationData): RegistrationData {
    const agreements = registration.agreements;
    registration.agreements = agreements.filter(agreement => agreement.details.hasOwnProperty('readonly') && !agreement.details.readonly);
    return registration;
  }

  private prepareMetrics(data: ProcessRequest): any {
    this.metricsService.sendSimpleMetric(CustomMetricsName.REGISTRATION_DATA, MetricsType.COLLECTED_DATA, JSON.stringify(data.body.registration));

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'submitOrderPolicy'
    };

    this.metricsService.sendRequestMetric(<RequestMetricName>{
      ...metricName,
      type: RequestMetricResult.SEND
    }, data);

    return metricName;
  }

  private sendPaymentEventMetric(): void {
    if (this.applicationStateService.salesChannel === SalesChannel.OLX) {
      this.olxAnalyticsService.sendPaymentEvent();
    }
  }
}

